import React, { useCallback, useState } from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { useStyles, createStyleSheet } from 'react-native-unistyles';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import ArmoryShiftDropdown from './ArmoryShiftDropdown';
import { AlreadyActiveArmourerOnUnit, armoryService } from '../../../services/central-api/armory';
import { ClientError, NotFound } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import FormInput from '../../../components/FormInput';
import ArmoryShiftReportModal from './ArmoryShiftReportModal';
import FormActions from '../../../components/FormActions';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';

export default function MyShifts() {
    const { styles, theme } = useStyles(styleSheet);
    const [isLoading, setIsLoading] = useState(true);
    const [armoryShiftId, setArmoryShiftId] = useState<number>(0);
    const [armoryShift, setArmoryShift] = useState<ArmoryShift | null>();
    const [previousArmoryShift, setPreviousArmoryShift] = useState<ArmoryShift | null>(null);
    const [alreadyActive, setAlreadyActive] = useState(true);

    const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);

    async function getPageInformation() {
        try {
            setIsLoading(true);
            const armourerActiveShift = await armoryService.getArmourerActiveShift();
            setArmoryShiftId(armourerActiveShift.id);
            setArmoryShift(armourerActiveShift);
            setAlreadyActive(true);

            const response = await armoryService.getArmourerPreviousShift();
            setPreviousArmoryShift(response);
            if (response && response.secondArmourerSignedOn == null && response.rejectionReason == null) {
                setReportModalVisible(true);
            }
        } catch (err) {
            if (err instanceof NotFound) {
                setAlreadyActive(false);
                setArmoryShift(null);
                setPreviousArmoryShift(null);
                setArmoryShiftId(0);
            }
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function setActiveShift() {
        try {
            if (armoryShiftId == 0) {
                return;
            }

            await armoryService.setArmourerActiveShift(armoryShiftId);
            await getPageInformation();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

        } catch (err) {
            if (err instanceof AlreadyActiveArmourerOnUnit) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                    visibilityTime: 10000,
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function saveDependenciesDocuments() {
        try {
            if (!armoryShift) {
                return;
            }

            await armoryService.saveDependenciesAndDocuments({
                armoryShiftId: armoryShift.id,
                documents: armoryShift.documents || null,
                dependencies: armoryShift.dependencies || null
            });
            await getPageInformation();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function endShift() {
        try {
            if (!armoryShift) {
                return;
            }

            await armoryService.saveDependenciesAndDocuments({
                armoryShiftId: armoryShift.id,
                documents: armoryShift.documents || null,
                dependencies: armoryShift.dependencies || null
            });
            setReportModalVisible(true);

        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function openHandoverReportUrl(armoryShiftId: number) {
        try {
            const res = await armoryService.getHandoverReportUrl(armoryShiftId);
            openExternalLinkInNewTab(res.resultUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getPageInformation();
        }
    }, [isFocused]));

    return <Container>
        <Header
            title={translate('myShifts')}
            actions={<CustomButton
                onPress={() => armoryShift ? endShift() : setActiveShift()}
                disabled={armoryShiftId == 0}
                text={armoryShift ? translate('endShift') : translate('startShift')} />}
        />
        <Card>
            {isLoading ? <ActivityIndicator size='large' style={styles.loadingContainer} color={theme.colors.dataTable.loading} /> :
                <ScrollView contentContainerStyle={styles.cardContainer}>
                    <View style={{ gap: 16, zIndex: 2 }}>
                        <View style={styles.activeShiftContainer}>
                            <View style={styles.rowContainer}>
                                <MyAppText style={styles.armoryText}>{translate('armory')}</MyAppText>
                                <MyAppText style={styles.statusText}>{alreadyActive ? translate('inActivity') : translate('notStarted')}</MyAppText>
                            </View>
                            <View style={styles.separator(theme.colors.primary['200'])}></View>
                        </View>
                        <ArmoryShiftDropdown
                            value={armoryShiftId}
                            setValue={setArmoryShiftId}
                            zIndex={2}
                            semiBoldLabel={true}
                            disabled={alreadyActive}
                        />
                    </View>
                    {armoryShift &&
                        <View style={styles.activeShiftContainer}>
                            <View style={styles.rowContainer}>
                                <MyAppText style={styles.armoryText}>{translate('shiftReport')}</MyAppText>
                                <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <View style={styles.badge('#D6D6D6')}>
                                        <MyAppText style={styles.badgeText('#515151')}>{translate('notSigned')}</MyAppText>
                                    </View>
                                    <View style={styles.badge('#F0D1AC')}>
                                        <MyAppText style={styles.badgeText('#8B4900')}>{translate('status_open')}</MyAppText>
                                    </View>
                                </View>
                            </View>
                            <View style={styles.separator(theme.colors.primary['200'])}></View>
                            <View style={{ gap: 16 }}>
                                <View style={{ gap: 8 }}>
                                    {previousArmoryShift &&
                                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <View style={{ flexDirection: 'row', gap: 3 }}>
                                                <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>
                                                    {`${translate('previousArmourer')}:`}
                                                </MyAppText>
                                                <MyAppText style={styles.text}>
                                                    {`${previousArmoryShift.user.warName || previousArmoryShift.user.name} -`}
                                                </MyAppText>
                                                <MyAppText style={[styles.text, { fontWeight: 'bold' }]}> RF: </MyAppText>
                                                <MyAppText style={styles.text}>
                                                    {previousArmoryShift.user.registry}
                                                </MyAppText>
                                            </View>
                                            <View style={styles.badge('#FDF6E5')}>
                                                <MyAppText style={styles.badgeText('#835E00')}>{moment(previousArmoryShift.definitiveStart).format('DD/MM/YYYY')}</MyAppText>
                                            </View>
                                            {previousArmoryShift.handoverReportUrl !== null &&
                                                <TouchableOpacity onPress={() => openHandoverReportUrl(previousArmoryShift.id)}>
                                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare as IconDefinition} fontSize={16} color={'#835E00'} />
                                                </TouchableOpacity>
                                            }
                                        </View>
                                    }
                                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <View style={{ flexDirection: 'row', gap: 3 }}>
                                            <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>
                                                {`${translate('armourerInCharge')}:`}
                                            </MyAppText>
                                            <MyAppText style={styles.text}>
                                                {`${armoryShift.user.warName || armoryShift.user.name} -`}
                                            </MyAppText>
                                            <MyAppText style={[styles.text, { fontWeight: 'bold' }]}> RF: </MyAppText>
                                            <MyAppText style={styles.text}>
                                                {armoryShift.user.registry}
                                            </MyAppText>
                                        </View>
                                        <View style={styles.badge('#FDF6E5')}>
                                            <MyAppText style={styles.badgeText('#835E00')}>{moment(armoryShift.definitiveStart).format('DD/MM/YYYY')}</MyAppText>
                                        </View>
                                    </View>
                                    {armoryShift.AssistantArmourers.length ? <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                        <View style={{ flexDirection: 'row', gap: 3 }}>
                                            <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>
                                                {`${translate('assistantsArmourer')}:`}
                                            </MyAppText>
                                            {armoryShift.AssistantArmourers.map((assistant) => {
                                                return (<View key={assistant.id} style={{ flexDirection: 'row' }}>
                                                    <MyAppText style={styles.text}>
                                                        {`${assistant.warName || assistant.name} -`}
                                                    </MyAppText>
                                                    <MyAppText style={[styles.text, { fontWeight: 'bold' }]}> RF: </MyAppText>
                                                    <MyAppText style={styles.text}>
                                                        {assistant.registry}
                                                    </MyAppText>
                                                </View>);
                                            })}
                                        </View>
                                    </View> : <></>}
                                </View>
                                <View style={{ gap: 10 }}>
                                    <FormInput
                                        label={`1. ${translate('dependencies')}`}
                                        labelFontSize={14}
                                        labelStyle={{ fontWeight: '600', color: theme.colors.neutral['500'] }}
                                        placeholder={translate('hasAnyDependency')}
                                        invalid={() => false}
                                        disabled={false}
                                        multiline={true}
                                        numberOfLines={3}
                                        maxLength={16777216}
                                        value={armoryShift.dependencies ?? ''}
                                        onChangeText={(value) => {
                                            setArmoryShift({ ...armoryShift, dependencies: value });
                                        }} />
                                    <FormInput
                                        label={`2. ${translate('documents')}`}
                                        placeholder={translate('addDocumentsInformation')}
                                        labelFontSize={14}
                                        labelStyle={{ fontWeight: '600', color: theme.colors.neutral['500'] }}
                                        invalid={() => false}
                                        disabled={false}
                                        multiline={true}
                                        maxLength={16777216}
                                        numberOfLines={6}
                                        value={armoryShift.documents ?? ''}
                                        onChangeText={(value) => {
                                            setArmoryShift({ ...armoryShift, documents: value });
                                        }} />
                                </View>
                                <FormActions
                                    onSubmit={() => saveDependenciesDocuments()}
                                    cancelText={'clearValues'}
                                    onClose={() => setArmoryShift({ ...armoryShift, documents: null, dependencies: null })}
                                    disabled={!armoryShift.documents && !armoryShift.dependencies}
                                />
                            </View>
                        </View>
                    }
                </ScrollView >
            }
        </Card>
        {armoryShift &&
            <ArmoryShiftReportModal
                armoryShift={armoryShift}
                isModalVisible={reportModalVisible}
                setModalVisible={setReportModalVisible}
                onSave={() => getPageInformation()}
                previousArmoryShift={previousArmoryShift}
            />}
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    activeShiftContainer: {
        flexDirection: 'column',
        maxHeight: 50,
        gap: 8
    },
    cardContainer: {
        flex: 1,
        gap: 32
    },
    rowContainer: {
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10
    },
    armoryText: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16
    },
    text: {
        color: theme.colors.neutral['400'],
        fontSize: 14
    },
    statusText: {
        color: theme.colors.neutral['400'],
        fontWeight: '600',
        fontSize: 12
    },
    separator: (color: string) => {
        return {
            borderBottomWidth: 1,
            borderColor: color,
            flex: 1,
        };
    },
    badge: (backgroundColor: string) => {
        return {
            backgroundColor,
            borderRadius: 4,
            paddingVertical: 4,
            paddingHorizontal: 8
        };
    },
    badgeText: (color: string) => {
        return {
            color,
            fontSize: 14
        };
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center'
    },
}));
